import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  LineEconomics,
  OrderEconomics,
  OrderListItemResponse,
  OrderResponse,
  StoreResponse,
} from '@clarity/contract';
import { useOrder, useOrders } from '../../queries/index.js';
import { QueryState } from '../QueryState.js';
import { isOnboardingRequired, money, num, pct, shortDate } from '../format.js';
import {
  AccuracyBadge,
  OnboardingBlocked,
  Row,
  SettlementBadge,
  SummaryGroupHead,
  SyncControl,
} from './shared.js';
import {
  OrderEconomicsRow,
  OrdersRollupTable,
  OrdersTableHead,
  computeRollup,
} from '../orders/OrdersEconomicsTable.js';

/**
 * The standalone Orders page for one onboarded store (clarity-orders-detail task
 * 4.1, Req 1.1–1.5). It is its OWN page at `/c/:clientId/<scope>/orders` — the
 * legacy `StoreData` `?tab=` tab host is retired. The page shows the orders list
 * and, when `?order=` is set, the full order detail drill-down (Req 1.4). The
 * list / detail BODIES are the ones that used to live inside the tab host,
 * unchanged apart from where they live.
 *
 * The order→payout trace now NAVIGATES across pages (Req 1.4; clarity-
 * navigation-scope Req 8.4): Orders and Payouts are SEPARATE pages, so following
 * the trace rewrites `?order=` off the Orders page and routes to the store's
 * Payouts page with `?payout=` set — a full surface change, not an in-page tab
 * switch.
 */
export function StoreOrders({ store }: { store: StoreResponse }) {
  // The opened order lives in `?order=` so it is linkable and part of the
  // back/forward stack (back closes the detail, a shared link reopens it).
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const openOrderId = searchParams.get('order');

  function setOpenOrderId(id: string | null) {
    const params = new URLSearchParams(searchParams);
    if (id) params.set('order', id);
    else params.delete('order');
    setSearchParams(params);
  }

  // An order→payout trace: the payout drill-down is its OWN PAGE now (no more
  // `?payout=` modal), so this navigates to that payout's page at
  // `.../store/:storeId/payouts/:payoutId` ({@link StorePayoutDetail}), dropping
  // the `?order=` detail (clarity-navigation-scope Req 8.4).
  function openPayout(payoutId: string) {
    if (!clientId) return;
    navigate(`/c/${clientId}/store/${store.id}/payouts/${payoutId}`);
  }

  return (
    <section>
      <SyncControl store={store} />
      {openOrderId ? (
        <OrderDetail
          store={store}
          orderId={openOrderId}
          onBack={() => setOpenOrderId(null)}
          onOpenPayout={openPayout}
        />
      ) : (
        <OrdersList store={store} onOpen={setOpenOrderId} />
      )}
    </section>
  );
}

// ─── Orders list ─────────────────────────────────────────────────────────────

function OrdersList({
  store,
  onOpen,
}: {
  store: StoreResponse;
  onOpen: (orderId: string) => void;
}) {
  const orders = useOrders(store.id);
  // The list read carries each order's economics (clarity-orders-detail Req
  // 4.1): rows are `{ order, economics }`. The single-branch page now renders
  // the SAME shared economics table as the combined view (minus the Store
  // column), so both presentations stay consistent (Req 1.5, 2.4, task 5.1).
  const rows: OrderListItemResponse[] = orders.data?.status === 200 ? orders.data.body : [];

  // The optional scope-level roll-up (Req 2.6): Σ commission / food cost /
  // profit / net payout across this store's rows (non-null figures only),
  // matching the Statements / Payouts combined roll-up precedent.
  const rollup = computeRollup(rows);

  if (isOnboardingRequired(orders.error)) {
    return <OnboardingBlocked />;
  }

  return (
    <QueryState
      isLoading={orders.isLoading}
      isError={orders.isError}
      label="orders"
    >
      {rows.length === 0 ? (
        <p className="muted">No orders yet.</p>
      ) : (
        <>
          <OrdersRollupTable rollup={rollup} />
          <table className="data-table combined-orders__table" aria-label="Orders">
            <OrdersTableHead withStore={false} />
            <tbody>
              {rows.map(({ order, economics }) => (
                <OrderEconomicsRow
                  key={order.id}
                  order={order}
                  economics={economics}
                  onOpen={() => onOpen(order.id)}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
    </QueryState>
  );
}

// ─── Order detail (Req 10.2) ──────────────────────────────────────────────────

function OrderDetail({
  store,
  orderId,
  onBack,
  onOpenPayout,
}: {
  store: StoreResponse;
  orderId: string;
  onBack: () => void;
  onOpenPayout: (payoutId: string) => void;
}) {
  const query = useOrder(store.id, orderId);
  // getById now returns the `{ order, economics }` detail envelope
  // (clarity-reconciliation Req 8.4), not a bare order.
  const detail = query.data?.status === 200 ? query.data.body : undefined;

  return (
    <section className="card" aria-label="Order detail">
      <button type="button" onClick={onBack}>
        ← Back to orders
      </button>
      {isOnboardingRequired(query.error) ? (
        <OnboardingBlocked />
      ) : (
        <QueryState isLoading={query.isLoading} isError={query.isError} label="order">
          {detail === undefined ? (
            <p className="muted">Order not found.</p>
          ) : (
            <OrderDetailBody
              order={detail.order}
              economics={detail.economics}
              onOpenPayout={onOpenPayout}
            />
          )}
        </QueryState>
      )}
    </section>
  );
}

function OrderDetailBody({
  order,
  economics,
  onOpenPayout,
}: {
  order: OrderResponse;
  economics: OrderEconomics;
  onOpenPayout: (payoutId: string) => void;
}) {
  const c = order.currency;
  // Settlement figures are null while an order is pending — the panels show "—"
  // for those, never a fabricated 0 (same rule as the list/roll-up).
  const pending = economics.totalSale === null;

  return (
    <div className="order-detail" aria-label="Order detail">
      {/* Headline: the order identity + its two axes, as the panel lead. */}
      <header className="order-detail__head">
        <div className="order-detail__title">
          <h3>Order {order.externalOrderId}</h3>
          <span className="muted">{shortDate(order.timestamp)}</span>
        </div>
        <div className="order-detail__badges">
          <SettlementBadge settlement={order.settlementStatus} />
          <AccuracyBadge accuracy={order.accuracy} />
        </div>
      </header>

      {economics.excludedFromAssertions && (
        <p className="notice warn" role="note">
          Excluded from reconciliation assertions
          {economics.exclusionReason ? ` — ${economics.exclusionReason}` : ''}.
        </p>
      )}
      {pending && (
        <p className="muted order-detail__pending" role="note">
          Settlement pending — settlement figures show “—” until Talabat reports them.
        </p>
      )}

      <div className="summary-groups__grid order-detail__grid">
        {/* Sale & commission — the top line + what Talabat takes. */}
        <section className="card summary-group" aria-label="Sale and commission">
          <SummaryGroupHead title="Sale & commission" />
          <div className="stat">
            <span className="stat__label">Total sale</span>
            <span className="stat__value num">{money(economics.totalSale, c)}</span>
          </div>
          <dl className="profile-grid">
            <Row label="Subtotal" value={money(economics.subTotal, c)} />
            <Row label="Commissionable GMV" value={money(economics.commissionableGmv, c)} />
            <Row label="Commission (pre-VAT)" value={money(economics.commissionPreVat, c)} />
            <Row label="Commission on payout" value={money(economics.commissionOnPayout, c)} />
          </dl>
        </section>

        {/* Payout — what lands, and the payout trace. */}
        <section className="card summary-group" aria-label="Payout">
          <SummaryGroupHead title="Payout" />
          <div className="stat">
            <span className="stat__label">Net payout</span>
            <span className="stat__value num">{money(economics.netPayout, c)}</span>
          </div>
          {order.payoutId ? (
            <p className="order-detail__trace">
              Settled by a payout —{' '}
              <button
                type="button"
                className="link"
                onClick={() => onOpenPayout(order.payoutId!)}
                aria-label={`Open the payout that settled order ${order.externalOrderId}`}
              >
                view reconciliation →
              </button>
            </p>
          ) : (
            <p className="muted">Not yet linked to a payout.</p>
          )}
        </section>

        {/* Discounts — who funded them (restaurant vs platform). */}
        <section className="card summary-group" aria-label="Discounts">
          <SummaryGroupHead title="Discounts" />
          <dl className="profile-grid">
            <Row
              label="Restaurant-funded"
              value={money(economics.restaurantFundedDiscount, c)}
            />
            <Row label="Platform-funded" value={money(economics.platformFundedDiscount, c)} />
          </dl>
        </section>

        {/* Food cost & margin — the owner-economics theme (the fix's payoff). */}
        <section className="card summary-group" aria-label="Food cost and margin">
          <SummaryGroupHead title="Food cost & margin" />
          <div className="stat">
            <span className="stat__label">Margin (total)</span>
            <span className="stat__value num">{money(economics.marginTotal, c)}</span>
          </div>
          <dl className="profile-grid">
            <Row label="Food cost (total)" value={money(economics.foodCostTotal, c)} />
          </dl>
        </section>
      </div>

      {/* Line economics — the per-item breakdown, kept as a wide table. */}
      <section className="card summary-group summary-group--wide" aria-label="Line economics">
        <SummaryGroupHead title="Line economics" />
        {economics.lines.length === 0 ? (
          <OrderLineItemsFallback order={order} />
        ) : (
          <table className="data-table data-table--wide" aria-label="Order line economics">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Qty</th>
                <th scope="col">Match</th>
                <th scope="col">Base price</th>
                <th scope="col">Allocated discount</th>
                <th scope="col">Food cost</th>
                <th scope="col">Commission</th>
                <th scope="col">Net revenue</th>
                <th scope="col">Margin</th>
              </tr>
            </thead>
            <tbody>
              {economics.lines.map((line, idx) => (
                <LineEconomicsRow key={`${line.externalItemId}-${idx}`} line={line} currency={c} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** One per-line economics row (Req 10.6) — matched/costed line with graceful nulls. */
function LineEconomicsRow({ line, currency }: { line: LineEconomics; currency: string }) {
  return (
    <tr>
      <td>{line.name}</td>
      <td>{num(line.quantity)}</td>
      <td>
        {line.resolved ? (
          <span className="badge" title={`matched by ${line.matchBasis}`}>
            {line.matchBasis === 'id' ? 'by id' : line.matchBasis === 'alias' ? 'by alias' : 'by name'}
          </span>
        ) : (
          <span className="badge badge-unresolved">unresolved</span>
        )}
      </td>
      <td>{money(line.basePrice, currency)}</td>
      <td>{money(line.allocatedDiscount, currency)}</td>
      <td>
        {money(line.foodCost, currency)}
        <span className="cell-sub"> {pct(line.foodCostPct)} · {line.foodCostBasis}</span>
      </td>
      <td>
        {money(line.commission, currency)}
        <span className="cell-sub"> {line.commissionBasis}</span>
      </td>
      <td>{money(line.netItemRevenue, currency)}</td>
      <td>{money(line.itemMargin, currency)}</td>
    </tr>
  );
}

/**
 * Fallback when the projection has not produced per-line economics yet: the
 * parsed order lines (catalog-linkage view). Keeps the detail useful for an
 * order whose economics are line-free (e.g. a settlement-pending projection).
 */
function OrderLineItemsFallback({ order }: { order: OrderResponse }) {
  if (order.lineItems.length === 0) return <p className="muted">No line items.</p>;
  return (
    <table className="data-table" aria-label="Order line items">
      <thead>
        <tr>
          <th scope="col">Item</th>
          <th scope="col">Qty</th>
          <th scope="col">Price</th>
          <th scope="col">Discount</th>
          <th scope="col">Food cost %</th>
          <th scope="col">Catalog</th>
        </tr>
      </thead>
      <tbody>
        {order.lineItems.map((line, idx) => (
          <tr key={`${line.externalItemId}-${idx}`}>
            <td>{line.name}</td>
            <td>{num(line.quantity)}</td>
            <td>{money(line.price, order.currency)}</td>
            <td>{money(line.discount, order.currency)}</td>
            <td>{pct(line.foodCostPctBasis)}</td>
            <td>
              {line.resolved ? (
                <span className="badge">linked</span>
              ) : (
                <span className="badge badge-unresolved">unresolved</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
